import React, { useEffect, useState } from 'react';
import { authenticatedFetch } from '../api/client';
import GeneratedWidget from './GeneratedWidget';

export async function builderRequest(path, options = {}) {
  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || ''}/api/assistant/builder${path}`, {
    ...options, headers: { 'Content-Type': 'application/json' },
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || 'Widget builder is unavailable.');
  return body;
}

export default function WidgetBuilder({ data, onPublish, onRemove, initialWidget = null, conversationMode = false, onSelect }) {
  const [widgets, setWidgets] = useState([]);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(initialWidget);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    builderRequest('/widgets', { signal: controller.signal }).then(result => {
      setWidgets(result.widgets); setFiles(result.sourceFiles);
    }).catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, []);
  function select(widget) { setSelected(widget); setPreview(null); setHistory(null); setError(''); onSelect?.(widget); }
  async function build(event) {
    event.preventDefault();
    if (busy || !prompt.trim()) return;
    setBusy(true); setError('');
    try {
      const result = await builderRequest('/draft', { method: 'POST', body: JSON.stringify({ prompt, reviseId: selected?.id, expectedRevision: selected?.revision }) });
      setWidgets(current => [result.widget, ...current.filter(w => w.id !== result.widget.id)]);
      setFiles(result.sourceFiles); select(result.widget); setPrompt('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function publish() {
    setBusy(true); setError('');
    try {
      const result = await builderRequest(`/widgets/${selected.id}/publish`, { method: 'POST', body: JSON.stringify({ expectedRevision: selected.revision || 1 }) });
      setSelected(result.widget);
      setWidgets(current => current.map(w => w.id === result.widget.id ? result.widget : w));
      onPublish(result.widget);
      onSelect?.(result.widget);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function loadHistory() {
    setBusy(true); setError('');
    try { setHistory(await builderRequest(`/widgets/${selected.id}/history`)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function changeVersion(operation, revision) {
    setBusy(true); setError('');
    try {
      const result = await builderRequest(`/widgets/${selected.id}/${operation}`, {
        method: 'POST', body: JSON.stringify({ revision, expectedRevision: selected.revision || 1 }),
      });
      setWidgets(current => [result.widget, ...current.filter(w => w.id !== result.widget.id)]);
      select(result.widget);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true); setError('');
    try {
      await builderRequest(`/widgets/${selected.id}`, { method: 'DELETE' });
      onRemove(selected.id); setWidgets(current => current.filter(w => w.id !== selected.id)); select(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  return <div className="widget-builder">
    {!conversationMode && <p>Describe a new widget. Gemini reads widget source files, writes JavaScript and CSS, and saves a private draft for you to preview.</p>}
    <details><summary>Source files Gemini can read</summary><ul>{files.map(file => <li key={file}>{file}</li>)}</ul></details>
    <label htmlFor="builder-saved">Your widgets and drafts</label>
    <select id="builder-saved" disabled={busy} value={selected?.id || ''} onChange={e => select(widgets.find(w => w.id === e.target.value) || null)}>
      <option value="">Create a new widget</option>{widgets.map(w => <option key={w.id} value={w.id}>{w.name} ({w.published ? 'on canvas' : 'draft'})</option>)}
    </select>
    {selected && <>
      <h4>{selected.name}</h4><p>{selected.description}</p>
      <p role="status">Saved privately · revision {selected.revision || 1}. Canvas changes require preview and approval.</p>
      <div className="widget-code-preview"><GeneratedWidget widget={selected} data={data} onResult={setPreview} /></div>
      <p role="status">{preview ? preview.message : 'Loading preview…'}</p>
      <details><summary>View generated code</summary><pre>{selected.source}</pre><pre>{selected.css}</pre></details>
      <div className="assistant-actions">
        <button type="button" disabled={busy || !preview?.ok} onClick={publish}>{selected.published ? 'Approve & update my canvas' : 'Approve & add to my canvas'}</button>
        <button type="button" disabled={busy} onClick={loadHistory}>History</button>
        <button type="button" disabled={busy} onClick={() => changeVersion('duplicate')}>Duplicate</button>
        <button type="button" disabled={busy} onClick={remove}>Delete {selected.published ? 'widget' : 'draft'}</button>
      </div>
      {history && <section aria-label="Widget history">
        <p>History storage: {(history.storageBytes / 1024).toFixed(1)} KB of {history.storageLimitBytes / 1024 / 1024} MB. Restore saves a new revision; preview it before updating the canvas.</p>
        {!history.history.length && <p>History begins with your next edit.</p>}
        <ul>{history.history.map(item => <li key={item.revision}>
          Revision {item.revision} · {item.operation} · {item.createdAt}{' '}
          <button type="button" disabled={busy || item.revision === selected.revision} onClick={() => changeVersion('restore', item.revision)}>Restore revision {item.revision}</button>
        </li>)}</ul>
      </section>}
    </>}
    {error && <p role="alert" className="assistant-error">{error}</p>}
    {!conversationMode && <form onSubmit={build}><label htmlFor="widget-build-prompt">{selected ? 'Describe an edit (autosaved with history)' : 'What should the new widget do?'}</label>
      <textarea id="widget-build-prompt" value={prompt} disabled={busy} maxLength={4000} rows={4} onChange={e => setPrompt(e.target.value)} placeholder="Create a bar chart comparing spike counts for the selected clusters, with a sort control." />
      <button type="submit" disabled={busy || !prompt.trim()}>{busy ? 'Building…' : selected ? 'Build revised draft' : 'Build new widget'}</button>
    </form>}
    {conversationMode && selected && <p>To revise this widget, describe your changes in the chat.</p>}
    <small>Uses a limited set of dashboard metrics. Widgets and revisions are private to your account. Editing keeps the same widget; Duplicate creates a separate copy. Deleting a widget also deletes its history. Core app files are not modified.</small>
  </div>;
}
